/**
 * Plant Zone Component
 * Visual representation of defined zones on plant layout
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { PlantZone as PlantZoneType, PlantLayout } from '../../services/plantMapping';
import { usePlantMappingStore } from '../../stores/plantMappingStore';

interface PlantZoneProps {
  zone: PlantZoneType;
  layout: PlantLayout;
  isSelected: boolean;
  canEditLayout?: boolean;
  onClick: () => void;
}

const PlantZone: React.FC<PlantZoneProps> = ({
  zone,
  layout,
  isSelected,
  canEditLayout = false,
  onClick
}) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [localZone, setLocalZone] = useState(zone);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeHandle, setResizeHandle] = useState<string | null>(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const zoneRef = useRef<SVGGElement>(null);
  
  const { deleteZone, updateZone } = usePlantMappingStore();
  // Update local zone when prop changes (but not during dragging or resizing)
  useEffect(() => {
    if (!isDragging && !isResizing) {
      setLocalZone(zone);
    }
  }, [zone, isDragging, isResizing]);

  // Convert normalized coordinates to SVG coordinates
  const x1 = localZone.x1 * layout.width;
  const y1 = localZone.y1 * layout.height;
  const x2 = localZone.x2 * layout.width;
  const y2 = localZone.y2 * layout.height;
  
  const width = x2 - x1;
  const height = y2 - y1;
  const centerX = x1 + width / 2;
  const centerY = y1 + height / 2;

  // Get stroke dash array based on border style
  const getStrokeDashArray = (style: string) => {
    switch (style) {
      case 'dashed':
        return '10,5';
      case 'dotted':
        return '2,3';
      case 'solid':
      default:
        return 'none';
    }
  };

  // Handle zone deletion
  const handleDeleteZone = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (showDeleteConfirm) {
      try {
        await deleteZone(zone.id);
      } catch (error) {
        console.error('Failed to delete zone:', error);
      }
    } else {
      setShowDeleteConfirm(true);
      // Auto-hide confirmation after 3 seconds
      setTimeout(() => setShowDeleteConfirm(false), 3000);
    }
  };

  const handleCancelDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowDeleteConfirm(false);
  };

  // Handle mouse down for dragging
  const handleMouseDown = useCallback((event: React.MouseEvent) => {
    if (!canEditLayout) return;
    // Don't start dragging if clicking on delete buttons
    if (showDeleteConfirm) return;
    
    event.preventDefault();
    event.stopPropagation();

    const svgElement = zoneRef.current?.closest('svg');
    if (!svgElement) return;

    const rect = svgElement.getBoundingClientRect();
    const svgPoint = svgElement.createSVGPoint();
    svgPoint.x = event.clientX - rect.left;
    svgPoint.y = event.clientY - rect.top;
    
    const svgCoords = svgPoint.matrixTransform(svgElement.getScreenCTM()?.inverse());
    
    setDragOffset({
      x: svgCoords.x - x1,
      y: svgCoords.y - y1
    });
    
    setIsDragging(true);
  }, [x1, y1, showDeleteConfirm, canEditLayout]);

  // Handle resize mouse down
  const handleResizeMouseDown = useCallback((event: React.MouseEvent, handle: string) => {
    if (!canEditLayout) return;
    event.preventDefault();
    event.stopPropagation();

    setIsResizing(true);
    setResizeHandle(handle);
    
    const svgElement = zoneRef.current?.closest('svg');
    if (!svgElement) return;

    const rect = svgElement.getBoundingClientRect();
    const svgPoint = svgElement.createSVGPoint();
    svgPoint.x = event.clientX - rect.left;
    svgPoint.y = event.clientY - rect.top;
    
    const svgCoords = svgPoint.matrixTransform(svgElement.getScreenCTM()?.inverse());
    
    setDragOffset({
      x: svgCoords.x,
      y: svgCoords.y
    });
  }, [canEditLayout]);

  // Handle global mouse events for dragging and resizing
  useEffect(() => {
    if (!isDragging && !isResizing) return;

    const handleMouseMove = (event: MouseEvent) => {
      const svgElement = zoneRef.current?.closest('svg');
      if (!svgElement) return;

      const rect = svgElement.getBoundingClientRect();
      const svgPoint = svgElement.createSVGPoint();
      svgPoint.x = event.clientX - rect.left;
      svgPoint.y = event.clientY - rect.top;
      
      const svgCoords = svgPoint.matrixTransform(svgElement.getScreenCTM()?.inverse());
      
      if (isDragging) {
        // Handle dragging (moving the zone)
        const newX1 = svgCoords.x - dragOffset.x;
        const newY1 = svgCoords.y - dragOffset.y;
        
        // Calculate zone dimensions
        const zoneWidth = localZone.x2 - localZone.x1;
        const zoneHeight = localZone.y2 - localZone.y1;
        
        // Convert to normalized coordinates and clamp to bounds
        const newNormX1 = Math.max(0, Math.min(1 - zoneWidth, newX1 / layout.width));
        const newNormY1 = Math.max(0, Math.min(1 - zoneHeight, newY1 / layout.height));
        const newNormX2 = newNormX1 + zoneWidth;
        const newNormY2 = newNormY1 + zoneHeight;
        
        setLocalZone({
          ...localZone,
          x1: newNormX1,
          y1: newNormY1,
          x2: newNormX2,
          y2: newNormY2
        });
      } else if (isResizing && resizeHandle) {
        // Handle resizing
        const mouseX = svgCoords.x / layout.width;
        const mouseY = svgCoords.y / layout.height;
        
        let newX1 = localZone.x1;
        let newY1 = localZone.y1;
        let newX2 = localZone.x2;
        let newY2 = localZone.y2;
        
        // Update coordinates based on resize handle
        switch (resizeHandle) {
          case 'nw': // Top-left corner
            newX1 = Math.max(0, Math.min(mouseX, localZone.x2 - 0.05));
            newY1 = Math.max(0, Math.min(mouseY, localZone.y2 - 0.05));
            break;
          case 'ne': // Top-right corner
            newX2 = Math.max(localZone.x1 + 0.05, Math.min(1, mouseX));
            newY1 = Math.max(0, Math.min(mouseY, localZone.y2 - 0.05));
            break;
          case 'sw': // Bottom-left corner
            newX1 = Math.max(0, Math.min(mouseX, localZone.x2 - 0.05));
            newY2 = Math.max(localZone.y1 + 0.05, Math.min(1, mouseY));
            break;
          case 'se': // Bottom-right corner
            newX2 = Math.max(localZone.x1 + 0.05, Math.min(1, mouseX));
            newY2 = Math.max(localZone.y1 + 0.05, Math.min(1, mouseY));
            break;
          case 'n': // Top edge
            newY1 = Math.max(0, Math.min(mouseY, localZone.y2 - 0.05));
            break;
          case 's': // Bottom edge
            newY2 = Math.max(localZone.y1 + 0.05, Math.min(1, mouseY));
            break;
          case 'w': // Left edge
            newX1 = Math.max(0, Math.min(mouseX, localZone.x2 - 0.05));
            break;
          case 'e': // Right edge
            newX2 = Math.max(localZone.x1 + 0.05, Math.min(1, mouseX));
            break;
        }
        
        setLocalZone({
          ...localZone,
          x1: newX1,
          y1: newY1,
          x2: newX2,
          y2: newY2
        });
      }
    };

    const handleMouseUp = async () => {
      const wasInteracting = isDragging || isResizing;
      
      setIsDragging(false);
      setIsResizing(false);
      setResizeHandle(null);
      
      // Update zone position/size in the backend if we were interacting
      if (wasInteracting) {
        try {
          await updateZone(localZone.id, {
            x1: localZone.x1,
            y1: localZone.y1,
            x2: localZone.x2,
            y2: localZone.y2
          });
        } catch (error) {
          console.error('Failed to update zone:', error);
          // Revert to original position/size on error
          setLocalZone(zone);
        }
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, isResizing, dragOffset, resizeHandle, localZone, layout, updateZone, zone]);

  return (
    <g
      ref={zoneRef}
      className={`plant-zone ${isSelected ? 'selected' : ''}`}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      onMouseDown={canEditLayout ? handleMouseDown : undefined}
      style={{ cursor: canEditLayout ? (isDragging ? 'grabbing' : isResizing ? 'crosshair' : 'move') : 'default' }}
    >
      {/* Zone background */}
      <rect
        x={x1}
        y={y1}
        width={width}
        height={height}
        fill={localZone.color}
        opacity={localZone.opacity}
        className="transition-opacity duration-200 hover:opacity-75"
      />

      {/* Zone border */}
      <rect
        x={x1}
        y={y1}
        width={width}
        height={height}
        fill="none"
        stroke={localZone.border_color}
        strokeWidth={isSelected ? "3" : "2"}
        strokeDasharray={getStrokeDashArray(localZone.border_style)}
        className="transition-all duration-200"
      />

      {/* Selection highlight */}
      {isSelected && (
        <rect
          x={x1 - 2}
          y={y1 - 2}
          width={width + 4}
          height={height + 4}
          fill="none"
          stroke="#3b82f6"
          strokeWidth="2"
          opacity="0.8"
        />
      )}

      {/* Zone label */}
      <text
        x={centerX}
        y={y1 - 5}
        textAnchor="middle"
        fill={localZone.border_color}
        fontSize="12"
        fontWeight="bold"
        style={{ pointerEvents: 'none', userSelect: 'none' }}
      >
        {localZone.name}
      </text>

      {/* Workshop count indicator */}
      {localZone.workshop_count > 0 && (
        <g>
          <circle
            cx={x2 - 15}
            cy={y1 + 15}
            r="10"
            fill={localZone.border_color}
            opacity="0.9"
          />
          <text
            x={x2 - 15}
            y={y1 + 15}
            textAnchor="middle"
            dominantBaseline="central"
            fill="white"
            fontSize="8"
            fontWeight="bold"
            style={{ pointerEvents: 'none', userSelect: 'none' }}
          >
            {localZone.workshop_count}
          </text>
        </g>
      )}

      {/* Capacity warning */}
      {localZone.is_over_capacity && (
        <g>
          <circle
            cx={x1 + 15}
            cy={y1 + 15}
            r="8"
            fill="#ef4444"
            opacity="0.9"
          />
          <text
            x={x1 + 15}
            y={y1 + 15}
            textAnchor="middle"
            dominantBaseline="central"
            fill="white"
            fontSize="10"
            fontWeight="bold"
            style={{ pointerEvents: 'none', userSelect: 'none' }}
          >
            !
          </text>
        </g>
      )}

      {/* Delete Zone Button */}
      {canEditLayout && (
      <g
        className="delete-zone-button"
        style={{ cursor: 'pointer' }}
        onClick={handleDeleteZone}
        opacity={isSelected ? "1" : "0.7"}
      >
        {showDeleteConfirm ? (
          // Confirmation state
          <g>
            <rect
              x={x2 - 80}
              y={y2 - 25}
              width="75"
              height="20"
              fill="#ef4444"
              rx="3"
              opacity="0.9"
            />
            <text
              x={x2 - 42.5}
              y={y2 - 12}
              textAnchor="middle"
              dominantBaseline="central"
              fill="white"
              fontSize="8"
              fontWeight="bold"
              style={{ pointerEvents: 'none', userSelect: 'none' }}
            >
              Click to Delete
            </text>
            <g
              style={{ cursor: 'pointer' }}
              onClick={handleCancelDelete}
            >
              <circle
                cx={x2 - 10}
                cy={y2 - 15}
                r="6"
                fill="#6b7280"
                opacity="0.8"
              />
              <text
                x={x2 - 10}
                y={y2 - 15}
                textAnchor="middle"
                dominantBaseline="central"
                fill="white"
                fontSize="8"
                style={{ pointerEvents: 'none', userSelect: 'none' }}
              >
                ✕
              </text>
            </g>
          </g>
        ) : (
          // Normal trash icon
          <g>
            <circle
              cx={x2 - 15}
              cy={y2 - 15}
              r="10"
              fill="#ef4444"
              opacity="0.8"
              className="hover:opacity-100"
            />
            <text
              x={x2 - 15}
              y={y2 - 15}
              textAnchor="middle"
              dominantBaseline="central"
              fill="white"
              fontSize="10"
              style={{ pointerEvents: 'none', userSelect: 'none' }}
            >
              🗑️
            </text>
          </g>
        )}
      </g>
      )}

      {/* Zone description (visible on hover or selection) */}
      {(isSelected || zone.description) && zone.description && (
        <text
          x={centerX}
          y={centerY}
          textAnchor="middle"
          dominantBaseline="central"
          fill={zone.border_color}
          fontSize="10"
          opacity="0.8"
          style={{ pointerEvents: 'none', userSelect: 'none' }}
        >
          {zone.description}
        </text>
      )}

      {/* Resize handles (visible when selected) */}
      {canEditLayout && isSelected && (
        <g className="resize-handles" opacity="0.7">
          {/* Corner handles */}
          <rect
            x={x1 - 3}
            y={y1 - 3}
            width="6"
            height="6"
            fill="#3b82f6"
            stroke="white"
            strokeWidth="1"
            style={{ cursor: 'nw-resize' }}
            onMouseDown={(e) => handleResizeMouseDown(e, 'nw')}
          />
          <rect
            x={x2 - 3}
            y={y1 - 3}
            width="6"
            height="6"
            fill="#3b82f6"
            stroke="white"
            strokeWidth="1"
            style={{ cursor: 'ne-resize' }}
            onMouseDown={(e) => handleResizeMouseDown(e, 'ne')}
          />
          <rect
            x={x1 - 3}
            y={y2 - 3}
            width="6"
            height="6"
            fill="#3b82f6"
            stroke="white"
            strokeWidth="1"
            style={{ cursor: 'sw-resize' }}
            onMouseDown={(e) => handleResizeMouseDown(e, 'sw')}
          />
          <rect
            x={x2 - 3}
            y={y2 - 3}
            width="6"
            height="6"
            fill="#3b82f6"
            stroke="white"
            strokeWidth="1"
            style={{ cursor: 'se-resize' }}
            onMouseDown={(e) => handleResizeMouseDown(e, 'se')}
          />

          {/* Edge handles */}
          <rect
            x={centerX - 3}
            y={y1 - 3}
            width="6"
            height="6"
            fill="#3b82f6"
            stroke="white"
            strokeWidth="1"
            style={{ cursor: 'n-resize' }}
            onMouseDown={(e) => handleResizeMouseDown(e, 'n')}
          />
          <rect
            x={centerX - 3}
            y={y2 - 3}
            width="6"
            height="6"
            fill="#3b82f6"
            stroke="white"
            strokeWidth="1"
            style={{ cursor: 's-resize' }}
            onMouseDown={(e) => handleResizeMouseDown(e, 's')}
          />
          <rect
            x={x1 - 3}
            y={centerY - 3}
            width="6"
            height="6"
            fill="#3b82f6"
            stroke="white"
            strokeWidth="1"
            style={{ cursor: 'w-resize' }}
            onMouseDown={(e) => handleResizeMouseDown(e, 'w')}
          />
          <rect
            x={x2 - 3}
            y={centerY - 3}
            width="6"
            height="6"
            fill="#3b82f6"
            stroke="white"
            strokeWidth="1"
            style={{ cursor: 'e-resize' }}
            onMouseDown={(e) => handleResizeMouseDown(e, 'e')}
          />
        </g>
      )}
    </g>
  );
};

export default PlantZone;
